import type { NightWindow } from '../city/NightWindow'
import { zonedParts, zonedWallClockToInstant } from './zonedTime'

/** Where an instant falls against a city's night window. */
export interface NightWindowState {
  /** Inside a window. */
  readonly active: boolean
  /** The window's close when `active`, else the next window's opening. */
  readonly nextBoundary: Date
}

const MS_PER_DAY = 24 * 60 * 60 * 1000

/**
 * The night window (`CityConfig.nightWindow`) at `now`, in `timeZone`. Both
 * ends are local wall-clock times: a DST change inside a window moves neither
 * (New York 2026-11-01 00:00 EDT → 06:30 EST is 7 h 30 min). A window may run
 * past midnight; one that opened on the previous local day still counts.
 * Pure.
 */
export function nightWindowState(
  now: Date,
  window: NightWindow,
  timeZone: string,
): NightWindowState {
  const today = zonedParts(now, timeZone)
  const nowTs = now.getTime()
  let nextStart = Number.POSITIVE_INFINITY
  // Calendar days from yesterday (a window still open past midnight) to a
  // week ahead (the next opening of a once-a-week window). Day arithmetic is
  // pure UTC calendar math; each day is then anchored in the city's zone.
  for (let offset = -1; offset <= 8; offset++) {
    const day = new Date(Date.UTC(today.year, today.month - 1, today.day) + offset * MS_PER_DAY)
    if (!window.days.includes(day.getUTCDay())) continue
    const y = day.getUTCFullYear()
    const m = day.getUTCMonth() + 1
    const d = day.getUTCDate()
    const start = zonedWallClockToInstant(y, m, d, window.startMinute, timeZone)
    const end = zonedWallClockToInstant(
      y,
      m,
      d,
      window.startMinute + window.durationMinutes,
      timeZone,
    )
    if (nowTs >= start && nowTs < end) return { active: true, nextBoundary: new Date(end) }
    if (start > nowTs) nextStart = Math.min(nextStart, start)
  }
  // A window opens at least once a week, so `nextStart` is always found; an
  // empty `days` list never opens — a day ahead keeps callers re-checking.
  return {
    active: false,
    nextBoundary: new Date(Number.isFinite(nextStart) ? nextStart : nowTs + MS_PER_DAY),
  }
}
